import {
  Component,
  Inject,
  AfterViewInit,
  Input,
  ChangeDetectionStrategy,
  OnDestroy,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { fromEventPattern, merge, Subscription } from 'rxjs';
import { map, startWith, scan, share } from 'rxjs/operators';
import { SwipeState } from '../models/swipe-state';
import { RxCSS } from '../rxcss';

@Component({
  selector: 'app-swipeable-list',
  templateUrl: './swipeable-list.component.html',
  styleUrls: ['./swipeable-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SwipeableListComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() titles: string[];
  @Input() colors: string[];
  @Input() activeItem: number;
  @Input() expanded = false;

  initialSetupDone = false;
  animationSubscrition: Subscription;
  machine = {
    START: {
      panup: 'NAV1',
      pandown: 'NAV1',
    },
    NAV1: {
      panup: 'NAV2',
      pandown: 'NAV1',
    },
    NAV2: {
      pandown: 'NAV1',
      panup: 'NAV2',
    }
  };

  constructor(@Inject('Flipping') public Flipping: any,
    @Inject('Hammer') public Hammer: any) {
  }

  ngAfterViewInit() {
    this.initialSetupDone = true;
    setTimeout(() => { this.setupApp(this.colors); }, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.activeItem && this.initialSetupDone === true) {
      setTimeout(() => { this.setupApp(this.colors); }, 0);
    }
  }

  ngOnDestroy() {
    if (this.animationSubscrition) {
      this.animationSubscrition.unsubscribe();
    }
  }

  private setupApp(colors) {
    const nativeNav = document.querySelector('.ui-nav');
    const nativeNavItems = document.querySelectorAll('.ui-nav-item');
    const nativeApp = document.querySelector('#app');
    const appRect = nativeApp.getBoundingClientRect();

    RxCSS.set(nativeApp as HTMLElement, {
      colors: this.colors,
    });

    const hNav = new this.Hammer(nativeNav, {
      direction: this.Hammer.DIRECTION_ALL,
    });

    hNav.get('pan')
      .set({ direction: this.Hammer.DIRECTION_ALL });

    const panMapping = map((event: any) => ({
      key: +event.target.getAttribute('data-key'),
      type: event.type,
      deltaX: event.deltaX,
      deltaY: event.deltaY
    }));

    const pan$ = panMapping(fromEventPattern(h => hNav.on('panstart panup pandown panleft panright panend', h)));

    const initialState = {
      appState: 'NAV2',
      changed: false,
      direction: undefined,
      dx: 0,
      dy: 0,
      n: this.activeItem || 1,
      panning: false,
      pending: false,
      prevState: 'NAV1',
      type: 'panend',
      x: 0
    };

    const Flipper = new this.Flipping({
      onFlip: ({ key, delta, node }) => RxCSS.set(node, { delta })
    });

    const setNthItemClass = (n) => {
      const navItem = nativeNavItems[n - 1];
      const prevNavItem = nativeNavItems[n - 2];

      const currActive = document.querySelector('.ui-nav-item.active');
      const currPrevActive = document.querySelector('.ui-nav-item.prev-active');
      if (currActive === navItem) { return; }

      if (currActive) { currActive.classList.remove('active'); }
      if (currPrevActive) { currPrevActive.classList.remove('prev-active'); }

      navItem.classList.add('active');
      if (prevNavItem) { prevNavItem.classList.add('prev-active'); }

      this.activeItem = n;
    };

    const state$ = merge(pan$).pipe(
      startWith(initialState),
      scan((state: SwipeState, action) => this.determineState(state, action)),
      share()
    );

    this.animationSubscrition = state$.subscribe((state: SwipeState) => {
      const end = state.type === 'panend';
      if (end) {
        Array.from(nativeNavItems).forEach(item => RxCSS.set(item as HTMLElement, { delta: { top: 0, left: 0 } }));
      }

      nativeNav['style']
        .setProperty('--nav-panning', +state.panning);

      if (state.changed) {
        Flipper.read();
        if (state.pending) {
          nativeApp.setAttribute('data-pending', 'true');
        } else {
          nativeApp.removeAttribute('data-pending');
        }
        nativeApp.setAttribute('data-state', state.appState);
        nativeApp.setAttribute('data-prev-state', state.prevState);

        Flipper.flip();
      }

      if (!state.pending) {
        nativeApp.removeAttribute('data-pending');
      }

      if (state.n) { setNthItemClass(state.n); }

      const px = state.dx / appRect.width;
      RxCSS.set(document.body, {
        gradient: colors[state.n || 0],
        pending: state.pending,
        'not-pending': !state.pending,
        panning: state.panning,
        'not-panning': !state.panning,
        n: state.n,
        x: `-${(state.n - 1) * 100}%`,
        dx: state.dx,
        dy: state.dy,
        px,
        'px-abs': Math.abs(state.dx / appRect.width),
        py: !state.prevState ? 0 : state.dy / {
          START: { NAV1: 75 },
          NAV1: { START: -75, NAV2: 75 },
          NAV2: { NAV1: -75 }
        }[state.prevState][state.appState],
      });
    });
  }

  determineState(state: SwipeState, action: any) {
    const clamp = (min, max) => val => Math.min(Math.max(min, val), max);
    let panning = {
      'panstart': true,
      'panend': false,
    }[action.type];
    if (panning === undefined) { panning = state.panning; }

    if (action.type === 'panstart' && state.appState === 'START') {
      return {
        ...state,
        n: action.key,
        direction: 'y',
      };
    }

    if (!state.pending && (action.type === 'panup' || action.type === 'pandown') && state.direction !== 'x') {
      return {
        ...state,
        prevState: state.appState,
        appState: this.machine[state.appState][action.type],
        direction: 'y',
        pending: true,
        panning: true,
        changed: true,
      };
    }

    if (state.pending && (action.type === 'panup' || action.type === 'pandown')) {
      let dy = state.dy;
      if (state.appState === 'NAV1' && state.prevState === 'START') {
        dy = clamp(-75, 0)(action.deltaY);
      } else if (state.appState === 'NAV1' && state.prevState === 'NAV2') {
        dy = clamp(0, 75)(action.deltaY);
      } else if (state.appState === 'NAV2') {
        dy = clamp(-75, 100)(action.deltaY);
      } else if (state.appState === 'START') {
        dy = clamp(0, 75)(action.deltaY);
      }

      return {
        ...state,
        dy,
      };
    }

    let n = state.n;
    if (action.type === 'panend') {
      if (state.appState === 'START') {
        n = undefined;
      } else if (state.dx > 100) {
        n = n - 1;
      } else if (state.dx < -100) {
        n = n + 1;
      }

      return {
        ...state,
        n: clamp(1, this.titles.length)(n),
        dx: 0,
        dy: 0,
        type: action.type,
        pending: false,
        panning: false,
        changed: false,
        direction: undefined,
      } as SwipeState;
    }

    const direction = action.type === 'panend'
      ? undefined
      : state.direction
      || {
        panleft: 'x',
        panright: 'x',
        panup: 'y',
        pandown: 'y',
        panend: undefined,
      }[action.type];

    return {
      ...state,
      n,
      direction,
      panning,
      type: action.type,
      x: action.type === 'panend'
        ? 0
        : state.x,
      dx: (action.type === 'panend' || direction === 'y')
        ? 0
        : action.deltaX || state.dx,
      dy: (action.type === 'panend' || direction === 'x')
        ? 0
        : action.deltaY || state.dy,
    };
  }
}
